import {
  StoreScopesModal,
  StoreScopesPreview,
  StoreScopesPreviewProps,
  useStoreMap,
} from 'components/StoreScopes';
import { Store, StoreScopesModalProps, StoreScopesProps } from 'components/StoreScopes';
import { noop } from 'lodash-es';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useTranslate } from '@tolgee/react';
import { Button, Popover } from 'antd';
import { Trigger } from './StoreList';
import moreSvg from '../assets/more.svg';
import { PreviewWrapper } from 'views/Assistant/Department/components/AssignReceptionConfColumn';

export function useStoreIds2StoreScopes({
  storeIds,
  onStoreIdsChange,
  storeMap: storeMapProp,
  storeDataSource,
}: Util.PartialRequired<
  Pick<StoreScopesProps, 'storeMap' | 'storeDataSource'>,
  'storeDataSource'
> & {
  storeIds: number[];
  onStoreIdsChange: (storeIds: number[]) => void;
}) {
  const storeIdListRef = useRef<number[]>([]);

  const [storeScopes, setStoreScopes] = useState<General.StoreScope[]>([]);

  const storeMap = useStoreMap({
    list: storeDataSource,
    map: storeMapProp,
  });

  useEffect(() => {
    const { current: preStoreIdList } = storeIdListRef;

    const storeIdNotChange = isEqual(preStoreIdList, storeIds);

    if (storeIdNotChange) {
      return;
    }

    storeIdListRef.current = storeIds;

    const storeScopes = storeIds.reduce<General.StoreScope[]>((res, storeId) => {
      const { [storeId]: store } = storeMap;

      if (!store) {
        return res;
      }

      const { externalType: platform, region } = store;

      const platformStoreScope = res.find((_) => _.platform === platform);

      if (!platformStoreScope) {
        res.push({
          platform,
          regionStoreScopes: [
            {
              region,
              storeIds: [storeId],
              storeCount: 1,
            },
          ],
          storeCount: 1,
        });

        return res;
      }

      platformStoreScope.storeCount += 1;

      const { regionStoreScopes } = platformStoreScope;

      const regionStoreScope = regionStoreScopes.find((_) => _.region === region);

      if (!regionStoreScope) {
        regionStoreScopes.push({
          region,
          storeIds: [storeId],
          storeCount: 1,
        });

        return res;
      }

      if (regionStoreScope.storeIds) {
        regionStoreScope.storeIds.push(storeId);
      } else {
        regionStoreScope.storeIds = [storeId];
      }

      regionStoreScope.storeCount += 1;

      return res;
    }, []);

    setStoreScopes(storeScopes);
  }, [storeIds, storeMap]);

  const onChange = useCallback(
    (storeScopes: General.StoreScope[]) => {
      setStoreScopes(storeScopes);

      const storeIdList = storeScopes
        .reduce<number[]>((res, storeScope) => {
          const { platform, regionStoreScopes } = storeScope;

          return regionStoreScopes.reduce((res, regionStoreScope) => {
            const { region, storeIds = [] } = regionStoreScope;

            if (region === 'all') {
              if (storeIds.length === 0) {
                res.push(
                  ...storeDataSource.filter((_) => _.externalType === platform).map((_) => _.id),
                );
              }

              return res;
            }

            if (storeIds.length === 0) {
              res.push(
                ...storeDataSource
                  .filter((_) => _.externalType === platform && _.region === region)
                  .map((_) => _.id),
              );

              return res;
            }

            res.push(...storeIds);

            return res;
          }, res);
        }, [])
        .sort();

      storeIdListRef.current = storeIdList;

      onStoreIdsChange(storeIdList);
    },
    [storeDataSource, onStoreIdsChange],
  );

  return {
    value: storeScopes,
    onChange,
    storeMap,
  };
}

export interface StoreIdsSelectModalProps
  extends Util.PartialRequired<
    Omit<StoreScopesModalProps, 'value' | 'onChange'>,
    'storeDataSource'
  > {
  value: number[];
  onChange: (value: number[]) => void;
}

export function StoreIdsSelectModal(props: StoreIdsSelectModalProps) {
  const {
    value: storeIds,
    onChange: onStoreIdsChange,
    storeMap: storeMapProp,
    storeDataSource,
    ...otherProps
  } = props;

  const { value, onChange, storeMap } = useStoreIds2StoreScopes({
    storeIds,
    onStoreIdsChange,
    storeMap: storeMapProp,
    storeDataSource,
  });

  return (
    <StoreScopesModal
      value={value}
      onChange={onChange}
      storeMap={storeMap}
      storeDataSource={storeDataSource}
      showFooterTips={false}
      {...otherProps}
    />
  );
}

export interface StoreIdsPreviewProps extends Omit<StoreScopesPreviewProps, 'value'> {
  value: number[];
  storeDataSource: Store[];
}

export function StoreIdsPreview(props: StoreIdsPreviewProps) {
  const { value: storeIds, storeDataSource, storeMap: storeMapProp, ...otherProps } = props;

  const { value, storeMap } = useStoreIds2StoreScopes({
    storeIds,
    onStoreIdsChange: noop,
    storeDataSource,
    storeMap: storeMapProp,
  });

  return <StoreScopesPreview value={value} storeMap={storeMap} {...otherProps} />;
}

export interface StoreIdsPreviewerProps extends StoreIdsPreviewProps {
  onClick?: () => void;
  emptyContent?: ReactNode;
}

export function StoreIdsPreviewer(props: StoreIdsPreviewerProps) {
  const { value, onClick, emptyContent, ...otherProps } = props;

  const { t } = useTranslate();

  if (value.length === 0) {
    return (
      <Trigger onClick={onClick}>{emptyContent || t('aide.x 个店铺', { x: value.length })}</Trigger>
    );
  }

  return (
    <Popover
      content={
        <PreviewWrapper>
          <StoreIdsPreview value={value} showSearch={false} showTitle={false} {...otherProps} />
        </PreviewWrapper>
      }
    >
      <Trigger onClick={onClick}>
        {t('aide.x 个店铺', { x: value.length })}
        <img src={moreSvg} alt="more" />
      </Trigger>
    </Popover>
  );
}

export function StoreIdsSelect(
  props: Pick<StoreIdsSelectModalProps, 'value' | 'onChange' | 'storeMap' | 'storeDataSource'>,
) {
  const { value, onChange, storeMap, storeDataSource } = props;

  const { t } = useTranslate();

  const [visible, setVisible] = useState(false);

  const modal = (
    <StoreIdsSelectModal
      visible={visible}
      onCancel={() => setVisible(false)}
      value={value}
      onChange={onChange}
      storeMap={storeMap}
      storeDataSource={storeDataSource}
    />
  );

  const preview = (
    <StoreIdsPreviewer
      value={value}
      storeMap={storeMap}
      storeDataSource={storeDataSource}
      onClick={() => setVisible(true)}
      emptyContent={
        <Button type="link" style={{ padding: 0 }}>
          {`+ ${t('storeGroup.请选择适用店铺')}`}
        </Button>
      }
    />
  );

  return (
    <>
      {modal}
      {preview}
    </>
  );
}
